import { HTMLProps, useCallback } from 'react';
import { css, Theme } from '@emotion/react';
import { ListItemFigure } from 'client/components/Other/ListItemFigure';
import { Switch } from 'client/components/Switch';
import { Typography } from 'client/components/Typography';
import { borderRadius, applyEffect, Transition } from 'client/Theme';

export interface ToggleProps extends Omit<HTMLProps<HTMLButtonElement>, 'onChange'> {
  title: string;
  subtitle?: string | JSX.Element;
  checked: boolean;
  icon?: JSX.Element | null;
  image?: { alt: string; src: string } | null;
  ornament?: JSX.Element | null;
  onChange?: (checked: boolean) => void;
}

export const Toggle = ({ title, subtitle, checked, icon, image, ornament, onChange, ...other }: ToggleProps) => {
  const handleChange = useCallback(() => onChange && onChange(!checked), [checked, onChange]);

  return (
    <button {...other} role="switch" aria-checked={checked} type="button" css={itemStyle} onClick={handleChange}>
      <ListItemFigure icon={icon} image={image} ornament={ornament} />
      <section>
        <Typography variant="body1" noWrap>
          {title}
        </Typography>
        {!!subtitle && (
          <Typography variant="body2" noWrap>
            {subtitle}
          </Typography>
        )}
      </section>
      <Switch checked={checked} tabIndex={-1} component="div" />
    </button>
  );
};

const itemStyle = (theme: Theme) => css`
  display: flex;
  position: relative;
  align-items: center;
  padding: calc(16px - 2px);
  width: 100%;
  min-height: 76px;
  background-color: transparent;
  border: 2px solid ${theme.color.grey[60]};
  border-radius: ${borderRadius}px;
  overflow: hidden;
  cursor: pointer;
  z-index: 1;

  box-sizing: border-box;

  transition: background-color ${Transition.basic};

  figure > .ornament > svg {
    transition: color ${Transition.basic};
  }

  &:disabled {
    padding: 13px;
    background-color: transparent;
    border: 3px solid ${theme.color.grey[60]};
    cursor: not-allowed;

    > div,
    > section {
      opacity: 0.8;
    }

    > p > .tag {
      margin-right: 6px;
      background-color: ${theme.color.grey[80]};
      color: ${theme.color.white} !important;
    }

    figure > .ornament > svg {
      color: ${theme.color.background};
    }
  }

  &:focus-visible {
    outline: solid 4px ${theme.color.primary[100]};
    outline-offset: 4px;
    z-index: 2;
  }

  &:hover:not(:disabled):not([aria-checked='true']) {
    background-color: ${applyEffect(theme.color.grey[60], 'hover')};

    figure > .ornament > svg {
      color: ${applyEffect(theme.color.grey[60], 'hover')};
    }
  }

  &[aria-checked='true'] {
    background-color: ${theme.color.grey[60]};

    figure > .ornament > svg {
      color: ${theme.color.grey[60]};
    }
  }

  > section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex: 1;
    margin-left: 16px;

    > .body1 {
      font-weight: bold;
      color: ${theme.color.foreground};
    }

    > .body2 {
      display: flex;
      align-items: center;

      > svg {
        margin-right: 3px;
      }
    }
  }

  > .switch {
    margin-right: 2px;
    pointer-events: none;
  }
`;
